import api from './api';

export interface Invoice {
  id: string;
  invoiceNumber: string;
  issueDate: string;
  status: 'DRAFT' | 'ISSUED' | 'SENT' | 'PAID' | 'CANCELLED';
  totalsGross: number;
  customerId: string;
}

export interface InvoiceExport {
  id: string;
  filePath: string;
  sha256: string;
  validated: boolean;
  validationErrors: any; // JSON object that may contain errors and warnings arrays
  validationWarnings?: any; // Deprecated - kept for backward compatibility, but warnings are now in validationErrors
}

export interface InvoiceExportResponse {
  success: boolean;
  export: InvoiceExport;
}

/**
 * Exportiert Rechnung als ZUGFeRD/Factur-X PDF
 */
export const exportInvoice = async (invoiceId: string): Promise<InvoiceExportResponse> => {
  const response = await api.post<InvoiceExportResponse>(`/invoices/${invoiceId}/export/zugferd`);
  return response.data;
};

/**
 * Lädt Rechnung als PDF herunter
 */
export const downloadInvoice = async (invoiceId: string): Promise<void> => {
  const response = await api.get(`/invoices/${invoiceId}/export/zugferd`, {
    responseType: 'blob',
  });

  // Erstelle Download-Link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;

  // Extrahiere Dateinamen aus Content-Disposition Header
  const contentDisposition = response.headers['content-disposition'];
  let filename = `invoice_${invoiceId}.pdf`;
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="(.+)"/);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }

  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Versendet Rechnung per E-Mail an Händler
 */
export const sendInvoice = async (
  invoiceId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await api.post<{ success: boolean; message: string }>(
    `/invoices/${invoiceId}/send`
  );
  return response.data;
};

/**
 * Lädt Invoice für eine Order
 */
export const getInvoiceByOrderId = async (orderId: string): Promise<InvoiceWithExport | null> => {
  try {
    const response = await api.get<InvoiceWithExport>(`/admin/orders/${orderId}/invoice`);
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null; // Keine Rechnung vorhanden
    }
    throw error;
  }
};

export interface InvoiceWithExport extends Invoice {
  exports?: Array<{
    id: string;
    validated: boolean;
    validationErrors: any; // JSON object that may contain errors and warnings
  }>;
}
